/* Copyright (c) 2020-present CloudIO Inc */

import {
  IoDatasourceAttributes,
  Query,
  Row,
} from '@cloudio-saas/datasource-types';
import { useStateXAtomSetter, useStateXAtomValue } from '@cloudio-saas/statex';
import {
  Checkbox,
  IconButton,
  ListItem,
  ListItemText,
  Theme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { FieldTypeIcons } from 'common/DatasourceAttributeTypeIcons';
import VirtualList from 'components/DataGrid/grid/List';
import { useAutoSizerContext } from 'hooks/useComponentSize';
import Icon, { parseIconText } from 'icons/Icon';
import {
  DSView,
  sidebarActiveAttributeIdAtom,
  sidebarActiveDatasourceIdAtom,
  sidebarAttributesFilterAtom,
  sidebarAttributesShowRequiredAtom,
  sidebarSelectedAttributesListAtom,
} from 'layout/Sidebar/atoms';
import { usePlatform } from 'platform/PlatformHooks';
import React, { CSSProperties } from 'react';
import { isViewOnlyDeveloper } from 'root/useUser';
import Store from 'store/Store';
import useAppStore from 'store/useAppStore';
import { getIoDatasourceAttributesStoreProps } from 'store/utils/utils';
import { stopPropagation } from 'utils/events';
import NoDataFound from 'utils/NoDataFound';
import TimeAgo from 'react-timeago';
import { UserProfilePic } from 'components/UserPic/UserPic';
import { useStateXAtom } from '@cloudio-saas/statex';
import { TypographyEllipsisProps } from 'utils/commonProps';

const useStyles = makeStyles((theme: Theme) => ({
  editIcon: {
    marginRight: 8,
    display: 'none',
  },
  listItem: {
    display: 'flex',
    alignItems: 'center',
    '&:hover': {
      '& > $editIcon': {
        display: 'flex',
      },
    },
  },
  active: {
    backgroundColor: theme.palette.action.selected,
  },
  icon: {
    marginRight: 8,
  },
  userIcon: {
    marginLeft: 6,
    marginBottom: 2,
    alignSelf: 'start',
    flexShrink: 0,
  },
}));

function AppCell({
  value,
  style,
  setView,
  store,
  label,
  active,
  record,
  isViewOnlyUser = false,
}: {
  value: string;
  label?: string;
  style?: CSSProperties;
  setView: (_view: DSView) => void;
  store: Store<IoDatasourceAttributes>;
  active: boolean;
  record: Row<IoDatasourceAttributes>;
  isViewOnlyUser: boolean;
}): React.ReactElement {
  const classes = useStyles();
  const setDsAttrId = useStateXAtomSetter(sidebarActiveAttributeIdAtom);
  const { iconName, iconType } = parseIconText(
    FieldTypeIcons[record.attributeType || 'String'],
  );

  const [selectedAttibutes, setSelectedAttibutes] = useStateXAtom(
    sidebarSelectedAttributesListAtom,
    { params: { ds: record.datasourceUid ?? '' } },
  );

  return (
    <ListItem
      data-testid={value}
      className={clsx(classes.listItem, active ? classes.active : null)}
      key={value}
      style={style}
      dense
      button
    >
      {!isViewOnlyUser ? (
        <Checkbox
          checked={
            record.attributeUid ? selectedAttibutes.includes(value) : false
          }
          onChange={() => {
            if (record.attributeUid) {
              selectedAttibutes.includes(value)
                ? setSelectedAttibutes(
                    selectedAttibutes.filter((id) => id !== value),
                  )
                : setSelectedAttibutes([...selectedAttibutes, value]);
            }
          }}
          disabled={isViewOnlyUser}
          sx={{ pr: '4px', marginRight: '4px', marginLeft: '-10px' }}
        />
      ) : null}
      <Icon className={classes.icon} iconName={iconName} iconType={iconType} />
      <ListItemText
        primaryTypographyProps={TypographyEllipsisProps}
        secondaryTypographyProps={TypographyEllipsisProps}
        primary={label || 'None'}
        secondary={
          <>
            <TimeAgo date={record.lastUpdateDate || ''} />
            <UserProfilePic
              userName={record.lastUpdatedBy}
              size={15}
              className={classes.userIcon}
              hideOnlineStatus={true}
            />
          </>
        }
        sx={{ userSelect: 'text', marginRight: '4px', cursor: 'pointer' }}
        onClick={(e) => {
          e.stopPropagation();
          setDsAttrId(value);
          store.setCurrentRecordId(value);
          setView('editChild');
        }}
      />
      {record.calculatedColumn === 'Y' && (
        <IconButton
          disableRipple
          onClick={stopPropagation}
          onMouseDown={stopPropagation}
          data-tip="Transient"
          sx={{ cursor: 'default' }}
        >
          <Icon iconName="tools" />
        </IconButton>
      )}
      {record.primaryKey === 'Y' && (
        <IconButton
          disableRipple
          onClick={stopPropagation}
          onMouseDown={stopPropagation}
          data-tip="Primary Key"
          sx={{ cursor: 'default' }}
        >
          <Icon iconName="key" />
        </IconButton>
      )}
      {record.allowBlank === 'N' && (
        <IconButton
          disableRipple
          onClick={stopPropagation}
          onMouseDown={stopPropagation}
          data-tip="Required"
          sx={{ cursor: 'default' }}
        >
          <Icon iconName="asterisk" height={12} width={12} />
        </IconButton>
      )}
    </ListItem>
  );
}

export const dsAttributesFilter: (
  appUid: string,
  datasourceUid: string,
) => Query<IoDatasourceAttributes> = (appUid, datasourceUid) => ({
  filter: [
    { appUid: { is: appUid } },
    { datasourceUid: { is: datasourceUid } },
  ],
  limit: 2000,
});

const AttributesList = ({
  setView,
  appUid,
}: {
  appUid: string;
  setView: (dsName: DSView) => void;
}) => {
  const { store, records, recordsOrder, currentRecordId } =
    useAppStore<IoDatasourceAttributes>(
      getIoDatasourceAttributesStoreProps(appUid),
    );
  const platform = usePlatform();
  const isViewOnlyUser = isViewOnlyDeveloper(platform, appUid);

  const dsid = useStateXAtomValue(sidebarActiveDatasourceIdAtom);
  store.read({ props: dsAttributesFilter(appUid, dsid) });

  let list = recordsOrder;
  const filter = useStateXAtomValue(sidebarAttributesFilterAtom);
  if (filter) {
    const lcfilter = filter.toLowerCase();
    list = recordsOrder
      .map((id) => records[id])
      .filter(
        (r) =>
          r &&
          ((r.attributeName &&
            r.attributeName.toLowerCase().indexOf(lcfilter) !== -1) ||
            (r.attributeCode &&
              r.attributeCode.toLowerCase().indexOf(lcfilter) !== -1) ||
            (r.attributeType &&
              r.attributeType.toLowerCase().indexOf(lcfilter) !== -1)),
      )
      .map((r) => (r._cid ?? r._id) as string);
  }

  const showReqFields = useStateXAtomValue(sidebarAttributesShowRequiredAtom);
  if (showReqFields && list.length > 0) {
    list = list
      .map((id) => records[id])
      .filter((r) => r.allowBlank === 'N')
      .map((r) => (r._cid ?? r._id) as string);
  }

  const { width, height } = useAutoSizerContext();
  if (list.length === 0) {
    return (
      <NoDataFound
        message={
          filter ? `No fields found matching "${filter}"!` : 'No fields found!'
        }
      />
    );
  }
  return (
    <VirtualList
      CellComponent={({ rowIndex, style }) => (
        <AppCell
          style={style}
          label={records[list[rowIndex]].attributeName}
          value={list[rowIndex]}
          setView={setView}
          store={store}
          active={currentRecordId === list[rowIndex]}
          record={records[list[rowIndex]]}
          isViewOnlyUser={isViewOnlyUser}
        />
      )}
      rowHeight={52}
      height={height}
      totalRows={list.length}
      width={width}
      custom
    />
  );
};

export default AttributesList;
